import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IUploadService } from "@shared/interfaces/upload-service.interface";
import { PopupActionType } from "@shared/types/popup/enums/popup-action-types.enum";
import { PopupDto } from "@shared/types/popup/popup.dto";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Statuses } from "../Popups";
import { PlanDto } from "@shared/types/plans/plan.dto";
import { PlanPlatform } from "@shared/types/plans/enum/plan-platform.enum";

const requiredFieldRule = {
  required: true,
  whitespace: true,
  message: "This field is required",
};

interface IModal {
  open: boolean;
  popup: PopupDto | null;
  onClose: () => void;
  onSave: (data: PopupDto) => void;
  isLoading: boolean;
  isError?: boolean;
  plans: PlanDto[];
}

const popupTypes = [
  { label: "Draft Tokens", value: PopupActionType.DRAFT_TOKENS },
  { label: "External Link", value: PopupActionType.EXTERNAL_LINK },
  { label: "Continue", value: PopupActionType.CONTINUE },
  { label: "In App Purchase", value: PopupActionType.IN_APP_PURCHASE },
];

const ChangePopupModal = ({
  open,
  popup,
  onClose,
  onSave,
  isLoading,
  isError,
  plans,
}: IModal) => {
  const isEditingMode = !!popup?.id;
  const [form] = Form.useForm();
  const [imageUploaded, setImageUploaded] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [actionOneType, setActionOneType] = useState<PopupActionType | null>(
    null,
  );
  const [actionTwoType, setActionTwoType] = useState<PopupActionType | null>(
    null,
  );

  const [androidPlans, iosPlans] = useMemo(() => {
    const iosPlans = plans.filter(
      (plan) => plan.revenueCatPlatform === PlanPlatform.IOS,
    );
    const androidPlans = plans.filter(
      (plan) => plan.revenueCatPlatform === PlanPlatform.ANDROID,
    );
    return [androidPlans, iosPlans];
  }, [plans]);

  const uploadService = appInject<IUploadService>(DI_TOKENS.uploadService);

  useEffect(() => {
    if (popup) {
      const { actionOne, actionTwo, data, imageUrl } = popup;

      setActionOneType(actionOne || null);
      setActionTwoType(actionTwo || null);

      form.setFieldsValue({
        ...popup,
        activeFrom: popup.activeFrom ? dayjs(popup.activeFrom) : undefined,
        activeTo: popup.activeTo ? dayjs(popup.activeTo) : undefined,
        actionOneValue: data?.actionOne ?? undefined,
        actionTwoValue: data?.actionTwo ?? undefined,
        imageUrl: imageUrl ?? undefined, // Set the imageUrl directly
      });
    }
  }, [form, popup]);

  const handleFinish = (values: any) => {
    const data: Record<string, any> = {};

    switch (values.actionOne) {
      case PopupActionType.DRAFT_TOKENS:
        data.actionOne = Number(values.actionOneValue);
        break;

      case PopupActionType.EXTERNAL_LINK:
        data.actionOne = values.actionOneValue;
        break;

      case PopupActionType.IN_APP_PURCHASE:
        data.actionOne = values.actionOneValue;
        break;

      default:
        delete data.actionOne;
    }

    switch (values.actionTwo) {
      case PopupActionType.DRAFT_TOKENS:
        data.actionTwo = Number(values.actionTwoValue);
        break;

      case PopupActionType.EXTERNAL_LINK:
        data.actionTwo = values.actionTwoValue;
        break;

      case PopupActionType.IN_APP_PURCHASE:
        data.actionTwo = values.actionTwoValue;
        break;

      default:
        delete data.actionTwo;
    }

    // Optionally include the imageUrl
    const payload: PopupDto = {
      ...values,
      activeFrom: values.activeFrom?.toDate() ?? null,
      activeTo: values.activeTo?.toDate() ?? null,
      data,
      imageUrl: values.imageUrl ?? null,
    };

    // Clean up temporary form-only fields
    delete (payload as any).actionOneValue;
    delete (payload as any).actionTwoValue;

    onSave(payload);
  };

  return (
    <Modal
      title={isEditingMode ? "Change Popup" : "Add Popup"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <Form
        form={form}
        requiredMark={false}
        layout="vertical"
        onFinish={handleFinish}
      >
        {isError && (
          <Form.Item>
            <Alert
              message="Error"
              description="An error occurred while saving the popup data. Please try again."
              type="error"
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item name="name" label="Name" rules={[requiredFieldRule]}>
          <Input />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={5} />
        </Form.Item>

        <Form.Item label="Image for Popup">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setImageUploaded(false);
              setImageFile(file);
              setImagePreview(file ? URL.createObjectURL(file) : null);
            }}
          />
          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <img
                src={imagePreview}
                alt="Selected"
                style={{ width: "100%", maxHeight: 200, objectFit: "contain" }}
              />

              {imageUploaded === false ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="primary"
                    disabled={imageUploading}
                    onClick={async () => {
                      if (!imageFile) return;
                      try {
                        setImageUploading(true);
                        const {
                          data: { url },
                        } = await uploadService.uploadImage(imageFile);
                        if (url) {
                          setImageUploaded(true);
                        }
                        form.setFieldsValue({ imageUrl: url });
                      } catch (err) {
                        console.error("Failed to upload image", err);
                      } finally {
                        setImageUploading(false);
                      }
                    }}
                    style={{ marginTop: 10 }}
                  >
                    {imageUploading ? "Uploading..." : "Use Image"}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </Form.Item>

        {/* Added imageUrl form field */}
        <Form.Item name="imageUrl" label="Image URL (Optional)">
          <Input type="url" />
        </Form.Item>

        <Form.Item
          name="actionOne"
          label="Action One"
          rules={[requiredFieldRule]}
        >
          <Select
            options={popupTypes}
            onChange={(value) => {
              setActionOneType(value);
              form.setFieldsValue({ actionOneValue: undefined });
            }}
          />
        </Form.Item>

        {actionOneType === PopupActionType.DRAFT_TOKENS && (
          <Form.Item
            name="actionOneValue"
            label="Action One Tokens Count"
            rules={[{ required: true, message: "Enter token count" }]}
          >
            <InputNumber
              min={1}
              style={{
                width: "100%",
              }}
            />
          </Form.Item>
        )}

        {actionOneType === PopupActionType.IN_APP_PURCHASE && (
          <>
            <Form.List
              name="actionOneValue"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length !== 2) {
                      return Promise.reject(
                        new Error("Both Android and iOS plans are required"),
                      );
                    }
                  },
                },
              ]}
            >
              {() => (
                <>
                  <Form.Item label="Action One Android Plan" required>
                    <Form.Item
                      name={0}
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please select Android plan",
                        },
                      ]}
                    >
                      <Select placeholder="Select Android Plan">
                        {androidPlans.map((plan) => (
                          <Select.Option key={plan.id} value={plan.id}>
                            {`${plan.name} (${plan.tokens})`}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Form.Item>

                  <Form.Item label="Action One iOS Plan" required>
                    <Form.Item
                      name={1}
                      noStyle
                      rules={[
                        { required: true, message: "Please select iOS plan" },
                      ]}
                    >
                      <Select placeholder="Select iOS Plan">
                        {iosPlans.map((plan) => (
                          <Select.Option key={plan.id} value={plan.id}>
                            {`${plan.name} (${plan.tokens})`}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        {actionOneType === PopupActionType.EXTERNAL_LINK && (
          <Form.Item
            name="actionOneValue"
            label="Action One External Link"
            rules={[{ required: true, message: "Enter a valid URL" }]}
          >
            <Input type="url" />
          </Form.Item>
        )}

        <Form.Item name="actionOneTitle" label="Action One Title">
          <Input />
        </Form.Item>

        <Form.Item name="actionTwo" label="Action Two">
          <Select
            options={popupTypes}
            onChange={(value) => {
              setActionTwoType(value);
              form.setFieldsValue({ actionTwoValue: undefined });
            }}
            allowClear
          />
        </Form.Item>

        {actionTwoType === PopupActionType.DRAFT_TOKENS && (
          <Form.Item
            name="actionTwoValue"
            label="Action Two Tokens Count"
            rules={[{ required: true, message: "Enter token count" }]}
          >
            <InputNumber
              min={1}
              style={{
                width: "100%",
              }}
            />
          </Form.Item>
        )}

        {actionTwoType === PopupActionType.IN_APP_PURCHASE && (
          <>
            <Form.List
              name="actionTwoValue"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length !== 2) {
                      return Promise.reject(
                        new Error("Both Android and iOS plans are required"),
                      );
                    }
                  },
                },
              ]}
            >
              {() => (
                <>
                  <Form.Item label="Action Two Android Plan" required>
                    <Form.Item
                      name={0}
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please select Android plan",
                        },
                      ]}
                    >
                      <Select placeholder="Select Android Plan">
                        {androidPlans.map((plan) => (
                          <Select.Option key={plan.id} value={plan.id}>
                            {`${plan.name} (${plan.tokens})`}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Form.Item>

                  <Form.Item label="Action Two iOS Plan" required>
                    <Form.Item
                      name={1}
                      noStyle
                      rules={[
                        { required: true, message: "Please select iOS plan" },
                      ]}
                    >
                      <Select placeholder="Select iOS Plan">
                        {iosPlans.map((plan) => (
                          <Select.Option key={plan.id} value={plan.id}>
                            {`${plan.name} (${plan.tokens})`}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        {actionTwoType === PopupActionType.EXTERNAL_LINK && (
          <Form.Item
            name="actionTwoValue"
            label="Action Two External Link"
            rules={[{ required: true, message: "Enter a valid URL" }]}
          >
            <Input type="url" />
          </Form.Item>
        )}

        <Form.Item name="actionTwoTitle" label="Action Two Title">
          <Input />
        </Form.Item>

        <Form.Item name="status" label="Status" rules={[requiredFieldRule]}>
          <Select options={Statuses} />
        </Form.Item>

        <Form.Item name="activeFrom" label="Active From">
          <DatePicker showTime inputReadOnly style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="activeTo" label="Active To">
          <DatePicker showTime inputReadOnly style={{ width: "100%" }} />
        </Form.Item>

        <Space
          style={{ justifyContent: "flex-end", width: "100%", marginTop: 15 }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEditingMode ? "Save" : "Add"}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ChangePopupModal;
